import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid-pro';
import moment from 'moment';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllEmployees } from '~/data/employees';
import { useGetFullEmploymentData } from '~/data/employment/useFullEmploymentData';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';

import { CaseStyles, toTitleCase } from './useToTitleCase';

type RewardingProposalColumnsProps = {
  employmentIds: number[];
};

const useCommonProposalColumnsUtils = (
  props: RewardingProposalColumnsProps,
) => {
  const { employmentIds } = props;

  const { selectedEmployeeId: employeeId } = useSelectedEmployeeId();
  // TODO In case of multiple employments, get currency info for all
  const { data } = useGetFullEmploymentData(
    {
      employeeId,
      employmentId: employmentIds[0],
    },
    undefined,
    !employmentIds[0],
  );

  const getEmploymentCurrency = useCallback(
    (employmentId: number) => {
      return employmentId === employmentIds[0]
        ? data?.emplBasic.fEmplCurrency ?? ''
        : '';
    },
    [data, employmentIds],
  );

  const { allEmployees } = useAllEmployees();
  const getApproverName = useCallback(
    (employeeId: number) =>
      `${allEmployees[employeeId].fEmpFirstName} ${allEmployees[employeeId].fEmpLastName}`,
    [allEmployees],
  );

  return {
    getApproverName,
    getEmploymentCurrency,
  };
};

export function useAllowanceProposalColumns(
  props: RewardingProposalColumnsProps,
): GridColDef[] {
  const { t } = useTranslation();

  const { getApproverName, getEmploymentCurrency } =
    useCommonProposalColumnsUtils(props);

  const columns: GridColDef[] = [
    {
      field: 'fEmplSubEnteredTime',
      headerName: t('laCreated'),
      valueGetter: (params: GridValueGetterParams) =>
        `${moment(params.row.fEmplSubEnteredTime).format(
          scheduledDateFormats.dotFormat,
        )}`,
      width: 120,
    },
    {
      field: 'fAllowanceProposalStatus',
      headerName: t('laStatus'),
      width: 120,
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fAllowanceProposalStatus)}`,
    },
    {
      field: 'fEmpSubEnteredEmpId',
      headerName: t('laApprover'),
      width: 130,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.fEmpSubEnteredEmpId
          ? getApproverName(params.row.fEmpSubEnteredEmpId)
          : t('laSystem'),
    },
    {
      field: 'fAllowanceProposalType',
      headerName: t('fAllowanceTypeLabel'),
      width: 165,
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fAllowanceProposalType, CaseStyles.snake)}`,
    },
    {
      field: 'fAllowanceProposalFrequency',
      headerName: t('laAllowanceFrequency'),
      width: 135,
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fAllowanceProposalFrequency)}`,
    },
    {
      field: 'fAllowanceProposalStartDate',
      headerName: t('laStartDate'),
      width: 135,
    },
    {
      field: 'fAllowanceProposalEndDate',
      headerName: t('laEndDate'),
      width: 130,
    },
    {
      field: 'fAllowanceProposalAmount',
      headerName: t('fAllowanceAmountLabel'),
      width: 130,
    },
    {
      field: 'fAllowanceProposalTaxableAmount',
      headerName: t('fAllowanceTaxableAmountLabel'),
      width: 120,
    },
    {
      field: 'fEmplCurrency',
      headerName: t('fCurrency'),
      width: 130,
      valueGetter: (params: GridValueGetterParams) =>
        getEmploymentCurrency(params.row.fEmplId),
    },
    {
      field: 'fAllowanceProposalInfo',
      headerName: t('laAllowanceInfo'),
    },
  ];
  return columns;
}

export function useBenefitProposalColumns(
  props: RewardingProposalColumnsProps,
): GridColDef[] {
  const { t } = useTranslation();

  const { getApproverName, getEmploymentCurrency } =
    useCommonProposalColumnsUtils(props);

  const columns: GridColDef[] = [
    {
      field: 'fEmplSubEnteredTime',
      headerName: t('laCreated'),
      valueGetter: (params: GridValueGetterParams) =>
        `${moment(params.row.fEmplSubEnteredTime).format(
          scheduledDateFormats.dotFormat,
        )}`,
      width: 120,
    },
    {
      field: 'fBenefitProposalStatus',
      headerName: t('laStatus'),
      width: 120,
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fBenefitProposalStatus)}`,
    },
    {
      field: 'fEmpSubEnteredEmpId',
      headerName: t('laApprover'),
      width: 130,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.fEmpSubEnteredEmpId
          ? getApproverName(params.row.fEmpSubEnteredEmpId)
          : t('laSystem'),
    },
    {
      field: 'fBenefitProposalType',
      headerName: t('fBenefitTypeLabel'),
      width: 165,
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fBenefitProposalType, CaseStyles.snake)}`,
    },
    {
      field: 'fBenefitProposalFrequency',
      headerName: t('fBenefitFrequencyLabel'),
      width: 135,
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fBenefitProposalFrequency)}`,
    },
    {
      field: 'fBenefitProposalStartDate',
      headerName: t('laStartDate'),
      width: 135,
    },
    {
      field: 'fBenefitProposalEndDate',
      headerName: t('laEndDate'),
      width: 130,
    },
    {
      field: 'fBenefitProposalAmount',
      headerName: t('fBenefitAmountLabel'),
      width: 130,
    },
    {
      field: 'fBenefitProposalTaxableAmount',
      headerName: t('fBenefitTaxableAmountLabel'),
      width: 120,
    },
    {
      field: 'fEmplCurrency',
      headerName: t('fCurrency'),
      width: 130,
      valueGetter: (params: GridValueGetterParams) =>
        getEmploymentCurrency(params.row.fEmplId),
    },
    {
      field: 'fBenefitProposalInfo',
      headerName: t('laAllowanceInfo'),
    },
    {
      field: 'fEmplAttachmentName',
      headerName: t('fBenefitAttachmentLabel'),
    },
  ];
  return columns;
}

export function useSalaryProposalColumns(
  props: RewardingProposalColumnsProps,
): GridColDef[] {
  const { t } = useTranslation();

  const { getApproverName, getEmploymentCurrency } =
    useCommonProposalColumnsUtils(props);

  const columns: GridColDef[] = [
    {
      field: 'fEmplSubEnteredTime',
      headerName: t('laCreated'),
      valueGetter: (params: GridValueGetterParams) =>
        `${moment(params.row.fEmplSubEnteredTime).format(
          scheduledDateFormats.dotFormat,
        )}`,
    },
    {
      field: 'fSalaryProposalStatus',
      headerName: t('laStatus'),
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fSalaryProposalStatus)}`,
    },
    {
      field: 'fEmpSubEnteredEmpId',
      headerName: t('laApprover'),
      width: 130,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.fEmpSubEnteredEmpId
          ? getApproverName(params.row.fEmpSubEnteredEmpId)
          : t('laSystem'),
    },
    {
      field: 'fSalaryProposalFrequency',
      headerName: t('fSalaryProposalFrequencyLabel'),
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fSalaryProposalFrequency)}`,
    },
    {
      field: 'fSalaryProposalSalary',
      headerName: t('fSalaryProposalSalaryLabel'),
    },
    {
      field: 'fEmplCurrency',
      headerName: t('fCurrency'),
      width: 130,
      valueGetter: (params: GridValueGetterParams) =>
        getEmploymentCurrency(params.row.fEmplId),
    },
    {
      field: 'fSalaryProposalChange',
      headerName: t('laRaiseAmount'),
    },
    {
      field: 'fSalaryProposalChangePct',
      headerName: t('laRaisePert'),
    },
    {
      field: 'fSalaryProposalStartDate',
      headerName: t('laStartDate'),
    },
    {
      field: 'fSalaryProposalEndDate',
      headerName: t('laEndDate'),
    },
    {
      field: 'fSalaryProposalType',
      headerName: t('fSalaryProposalSalaryTypeLabel'),
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(params.row.fSalaryProposalType, CaseStyles.snake)}`,
    },
    {
      field: 'fSalaryProposalSalaryClass',
      headerName: t('fSalaryProposalSalaryClassLabel'),
    },
    {
      field: 'fSalaryProposalChangeReason',
      headerName: t('laRaiseReason'),
      valueGetter: (params: GridValueGetterParams) =>
        `${toTitleCase(
          params.row.fSalaryProposalChangeReason,
          CaseStyles.snake,
        )}`,
    },
    {
      field: 'fEmplAttachmentName',
      headerName: t('laAttachment'),
    },
  ];
  return columns;
}
